import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import WATER_LAND_IMAGE from "../image/Land_pozo.png";
import WOOD_LAND_IMAGE from "../image/madera.png";
import ENERGY_LAND_IMAGE from "../image/Land_Electrica.png";
import CHICKEN_LAND_IMAGE from "../image/land_gallinas.png";
import CORN_LAND_IMAGE from "../image/pasto.png";
import WHEAT_LAND_IMAGE from "../image/tieraTrigo.png";
import COW_LAND_IMAGE from "../image/Land_vacas.png";

/*
======================================================
 CITY FARM - VISIT FARMS MODE
 Single frontend file for:
 - Opening the Visit Farms panel from an NPC or button
 - Listing farms from other players
 - Filters/search/sort
 - Showing each farm with a farm image
 - Working on farms: Harvest / Plant / Collect Water
 - Showing worker history and owner activity
 - Reading the token from cookies
 - Keeping all styles inside this same file
======================================================
*/

/* ===================== CONFIG ===================== */

const FARM_IMAGE =
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767752473/LVL5_ppgq3x.png";

const API_BASE = "/api/cityfarm/city";
const API_TIMEOUT_MS = 10000;
const API_ACTION_TIMEOUT_MS = 15000;

const COLOR_INFO = {
  cyan: {
    label: "Water ready",
    className: "cf-city-status-cyan",
    emoji: "💧",
  },
  blue: {
    label: "Ready to plant",
    className: "cf-city-status-blue",
    emoji: "🌱",
  },
  green: {
    label: "Ready to harvest",
    className: "cf-city-status-green",
    emoji: "🌾",
  },
  yellow: {
    label: "In progress",
    className: "cf-city-status-yellow",
    emoji: "⏳",
  },
  purple: {
    label: "Animal production",
    className: "cf-city-status-purple",
    emoji: "🐄",
  },
  red: {
    label: "No actions",
    className: "cf-city-status-red",
    emoji: "⛔",
  },
  gray: {
    label: "Unavailable",
    className: "cf-city-status-gray",
    emoji: "🏗️",
  },
};

const LAND_ACTION_TEXT = {
  harvest: {
    title: "Harvest",
    emoji: "🌾",
    description: "Owner receives production. You receive Water/Croquettes.",
  },
  plant: {
    title: "Plant",
    emoji: "🌱",
    description: "Uses owner's seeds and Water. You receive a reward.",
  },
  collect_water: {
    title: "Collect Water",
    emoji: "💧",
    description: "Owner receives Water. You receive a helper reward.",
  },
  collect_energy: {
    title: "Collect Energy",
    emoji: "⚡",
    description: "Owner receives Energy. You receive a helper reward.",
  },
  collect_resource: {
    title: "Collect Resource",
    emoji: "📦",
    description: "Owner receives production. You receive a helper reward.",
  },
};

const DEFAULT_SEEDS = 1;

const CORN_LEVEL_IMAGES = [
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767277907/pasto.b9b7aae7a28230fccbb6_pgkcfm.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767278210/lvl2_rojyue.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767278212/lvl3_zioeyi.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767278212/lvl4_fhxhah.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767278287/lvl5_c8ifrc.png",
];

const WHEAT_LEVEL_IMAGES = [
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767275202/tieraTrigo.cb46df694f72f63aabb4_g02e5k.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767276766/lvl2_hvk6rn_iqey5g.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767276766/lvl_3_gvzzie_d5f8vb.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767276768/lvl_4_p871g9_eyg9lt.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767276767/lvl5_lmykkk_bwynot.png",
];

const WATER_LEVEL_IMAGES = [
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767287128/lvl1_mgtynd.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767287129/lvl2_rchvkc.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767287131/lvl3_fzeegn.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767287129/lvl4_yvysld.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767287130/lvl5_rlqhcf.png",
];

const TREE_LEVEL_IMAGES = [
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767285920/lvl1_q4eyov.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767285923/lvl2_docxz1.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767285923/lvl3_qmm43n.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767285925/lvl4_bhqffb.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767285928/lvl5_owxm6p.png",
];

const ENERGY_LEVEL_IMAGES = [
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767324324/lvl1_fnfhze.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767324327/lvl2_yjkpyu.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767324325/lvl3_e19aro.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767324326/lvl4_rcf5ew.png",
  "https://res.cloudinary.com/dkm71aag8/image/upload/v1788703107/logos/enerigia_lv5_xmvtgx.png",
];

const COOP_LEVEL_IMAGES = [
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767279773/lvl1_fzyo2i.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767279775/lvl2_tcqkuz.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767279775/lvl3_p93byw.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767279776/lvl4_viwota.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767279779/lvl5_ez5sdm.png",
];

const COW_LEVEL_IMAGES = [
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767280654/lvl1_kpvrhn.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767280655/lvl2_xbatel.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767280669/lvl3_ngb7zk.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767280670/lvl4_skdphg.png",
  "https://res.cloudinary.com/dpwv380jp/image/upload/v1767280671/lvl5_gmfkfr.png",
];

const DEFAULT_LAND_IMAGES = {
  water: WATER_LAND_IMAGE,
  agua: WATER_LAND_IMAGE,
  corn: CORN_LAND_IMAGE,
  maiz: CORN_LAND_IMAGE,
  maize: CORN_LAND_IMAGE,
  wheat: WHEAT_LAND_IMAGE,
  trigo: WHEAT_LAND_IMAGE,
  wood: WOOD_LAND_IMAGE,
  madera: WOOD_LAND_IMAGE,
  energy: ENERGY_LAND_IMAGE,
  energia: ENERGY_LAND_IMAGE,
  gallinero: CHICKEN_LAND_IMAGE,
  chicken: CHICKEN_LAND_IMAGE,
  gallina: CHICKEN_LAND_IMAGE,
  cow: COW_LAND_IMAGE,
  vaca: COW_LAND_IMAGE,
  seed: CORN_LAND_IMAGE,
  default: FARM_IMAGE,
};

/* ===================== HELPERS ===================== */

function getCookie(name) {
  if (typeof document === "undefined") return "";

  const cookies = document.cookie.split(";").map((cookie) => cookie.trim());

  for (const cookie of cookies) {
    if (cookie.startsWith(name + "=")) {
      return decodeURIComponent(cookie.substring(name.length + 1));
    }
  }

  return "";
}

function getStoredToken() {
  const cookieToken = getCookie("token");
  if (cookieToken) return cookieToken;

  if (typeof window === "undefined") return "";

  return (
    window.sessionStorage?.getItem("token") ||
    window.localStorage?.getItem("token") ||
    ""
  );
}

async function safeJsonResponse(response) {
  const text = await response.text();

  if (!text) {
    return {
      ok: response.ok,
      status: response.status,
      data: {},
      raw: "",
    };
  }

  try {
    return {
      ok: response.ok,
      status: response.status,
      data: JSON.parse(text),
      raw: text,
    };
  } catch {
    return {
      ok: false,
      status: response.status,
      data: null,
      raw: text,
    };
  }
}

async function apiRequest(path, options = {}, timeoutMs = API_TIMEOUT_MS) {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  try {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      signal: controller?.signal,
    });

    return safeJsonResponse(response);
  } catch (error) {
    const timedOut = error?.name === "AbortError";
    return {
      ok: false,
      status: 0,
      data: {
        error: timedOut
          ? "City Farm server took too long to respond. Please retry."
          : "Network error while contacting City Farm.",
      },
      raw: "",
      networkError: true,
      timedOut,
    };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function apiGet(path) {
  const token = getStoredToken();

  return apiRequest(path, {
    method: "GET",
    headers: { token },
    cache: "no-store",
  });
}

async function apiPost(path, body) {
  const token = getStoredToken();

  return apiRequest(
    path,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        token,
      },
      body: JSON.stringify(body || {}),
    },
    API_ACTION_TIMEOUT_MS
  );
}

async function apiGetWithFallback(primaryPath, fallbackPath) {
  const primary = await apiGet(primaryPath);

  if (
    primary.status !== 404 &&
    !String(primary.raw || "").toLowerCase().includes("<!doctype")
  ) {
    return primary;
  }

  return apiGet(fallbackPath);
}

async function apiPostWithFallback(primaryPath, fallbackPath, body) {
  const primary = await apiPost(primaryPath, body);

  if (
    primary.status !== 404 &&
    !String(primary.raw || "").toLowerCase().includes("<!doctype")
  ) {
    return primary;
  }

  return apiPost(fallbackPath, body);
}

function getStatusInfo(color) {
  return COLOR_INFO[color] || COLOR_INFO.red;
}

function shortName(value = "") {
  if (!value) return "Unknown Farm";
  if (value.length <= 18) return value;
  return value.slice(0, 16) + "...";
}

function buildErrorMessage(result, fallback) {
  if (result.status === 401) {
    return "Session expired or token not found. Please log in again.";
  }

  if (String(result.raw || "").toLowerCase().includes("<!doctype")) {
    return "The backend route is not connected. The server returned the game page.";
  }

  return result.data?.error || result.data?.message || fallback;
}

function normalizeImageUrl(value) {
  if (typeof value !== "string") return "";

  const clean = value.trim();
  if (!clean) return "";

  if (
    clean.startsWith("http://") ||
    clean.startsWith("https://") ||
    clean.startsWith("/") ||
    clean.startsWith("data:image/")
  ) {
    return clean;
  }

  return "";
}

function clampLevelIndex(level, max) {
  const numericLevel = Number(level || 1);
  const safeLevel = Number.isFinite(numericLevel) && numericLevel > 0 ? numericLevel : 1;
  return Math.max(0, Math.min(max - 1, Math.floor(safeLevel) - 1));
}

function getLandSearchText(land) {
  return [
    land?.produce,
    land?.produccionItem,
    land?.item,
    land?.crop,
    land?.cultivo,
    land?.key,
    land?.name,
    land?.nombre,
    land?.type,
    land?.tipo,
    land?.category,
    land?.categoria,
    land?.text,
    land?.texto,
    land?.status,
    land?.state,
    land?.estado,
  ]
    .filter(Boolean)
    .join(" ")
    .trim()
    .toLowerCase();
}

function getLeveledImage(images, level, fallback) {
  if (!Array.isArray(images) || !images.length) return fallback;
  return images[clampLevelIndex(level, images.length)] || fallback;
}

function getLandImage(land) {
  if (!land) return FARM_IMAGE;

  const explicitImage = normalizeImageUrl(
    land.image ||
      land.img ||
      land.imagen ||
      land.foto ||
      land.icon ||
      land.icono ||
      land.thumbnail ||
      land.thumb ||
      land.imageUrl ||
      land.urlImagen ||
      land.imagenUrl ||
      land?.metadata?.image ||
      land?.properties?.image
  );

  if (explicitImage) return explicitImage;

  const searchText = getLandSearchText(land);
  const level = land.level || land.nivel || 1;
  const id = Number(land?.id || land?.landId || land?.tierraId || 0);

  // ID manda primero. Así Energy #9 no cae en Water por status legacy "ready_to_collect_water".
  if (
    id === 9 ||
    searchText.includes("energy") ||
    searchText.includes("energia") ||
    searchText.includes("energía") ||
    searchText.includes("rayo") ||
    searchText.includes("electric")
  ) {
    return getLeveledImage(ENERGY_LEVEL_IMAGES, level, DEFAULT_LAND_IMAGES.energy);
  }

  if (id === 8 || id === 12 || searchText.includes("water") || searchText.includes("agua") || searchText.includes("pozo")) {
    return getLeveledImage(WATER_LEVEL_IMAGES, level, DEFAULT_LAND_IMAGES.water);
  }

  if (
    searchText.includes("corn") ||
    searchText.includes("maiz") ||
    searchText.includes("maíz") ||
    searchText.includes("maize")
  ) {
    return getLeveledImage(CORN_LEVEL_IMAGES, level, DEFAULT_LAND_IMAGES.corn);
  }

  if (searchText.includes("wheat") || searchText.includes("trigo")) {
    return getLeveledImage(WHEAT_LEVEL_IMAGES, level, DEFAULT_LAND_IMAGES.wheat);
  }

  if (
    searchText.includes("wood") ||
    searchText.includes("madera") ||
    searchText.includes("arbol") ||
    searchText.includes("árbol")
  ) {
    return getLeveledImage(TREE_LEVEL_IMAGES, level, DEFAULT_LAND_IMAGES.wood);
  }

  if (
    searchText.includes("gallinero") ||
    searchText.includes("gallina") ||
    searchText.includes("chicken") ||
    searchText.includes("egg") ||
    searchText.includes("huevo")
  ) {
    return getLeveledImage(COOP_LEVEL_IMAGES, level, DEFAULT_LAND_IMAGES.gallinero);
  }

  if (
    searchText.includes("vaca") ||
    searchText.includes("cow") ||
    searchText.includes("milk") ||
    searchText.includes("leche")
  ) {
    return getLeveledImage(COW_LEVEL_IMAGES, level, DEFAULT_LAND_IMAGES.cow);
  }

  if (land.canCollectWater || land.puedeRecogerAgua) return getLeveledImage(WATER_LEVEL_IMAGES, level, DEFAULT_LAND_IMAGES.water);
  if (land.canHarvest || land.puedeCosechar) return DEFAULT_LAND_IMAGES.corn;
  if (land.canPlant || land.puedeSembrar) return DEFAULT_LAND_IMAGES.corn;

  return DEFAULT_LAND_IMAGES.default;
}

function getResourceKind(land) {
  const text = String(
    land?.produce ||
      land?.produccionItem ||
      land?.item ||
      land?.key ||
      land?.name ||
      land?.nombre ||
      land?.text ||
      land?.texto ||
      ""
  ).toLowerCase();

  const id = Number(land?.id || land?.landId || land?.tierraId || 0);

  if (id === 9 || text.includes("energy") || text.includes("energia") || text.includes("energía") || text.includes("rayo") || text.includes("electric")) {
    return "energy";
  }

  if (id === 8 || id === 12 || text.includes("water") || text.includes("agua") || text.includes("pozo") || text.includes("well")) {
    return "water";
  }

  return "resource";
}

function getLandEmoji(land) {
  const text = String(land?.produce || land?.key || land?.text || "").toLowerCase();

  if (getResourceKind(land) === "energy") return "⚡";
  if (getResourceKind(land) === "water") return "💧";
  if (text.includes("corn") || text.includes("maiz") || text.includes("maíz")) return "🌽";
  if (text.includes("wheat") || text.includes("trigo")) return "🌾";
  if (text.includes("wood") || text.includes("madera")) return "🪵";
  if (land?.canPlant) return "🌱";
  if (land?.canHarvest) return "🌾";
  if (land?.canCollectEnergy || land?.canCollectResource) return getResourceKind(land) === "energy" ? "⚡" : "📦";
  if (land?.canCollectWater) return getResourceKind(land) === "energy" ? "⚡" : "💧";

  return "🏡";
}

function getAvailableLandActions(land) {
  if (!land) return [];

  const actions = [];
  const kind = getResourceKind(land);

  if (land.canCollectEnergy || land.canCollectResource || land.canCollectWater) {
    if (kind === "energy") actions.push("collect_energy");
    else if (kind === "water") actions.push("collect_water");
    else actions.push("collect_resource");
  }

  if (land.canHarvest) actions.push("harvest");
  if (land.canPlant) actions.push("plant");

  return [...new Set(actions)].filter((action) => LAND_ACTION_TEXT[action]);
}

function getFarmPreviewImage(farm) {
  const firstLandWithImage = (farm?.lands || [])
    .map(normalizeLand)
    .find((land) => normalizeImageUrl(land.image));

  return firstLandWithImage ? getLandImage(firstLandWithImage) : FARM_IMAGE;
}

function normalizeFarms(payload) {
  const farms =
    payload?.farms ||
    payload?.granjas ||
    payload?.data?.farms ||
    payload?.data?.granjas ||
    [];

  if (!Array.isArray(farms)) return [];

  return farms.map((farm) => {
    const lands = Array.isArray(farm.lands)
      ? farm.lands
      : Array.isArray(farm.tierras)
      ? farm.tierras
      : [];

    return {
      owner: farm.owner || farm.nombre || farm.name || farm.propietario || "",
      email: farm.email || farm.correo || "",
      color: farm.color || farm.mainColor || "red",

      available: Number(farm.available ?? farm.disponibles ?? farm.actionsAvailable ?? 0),
      harvestAvailable: Number(farm.harvestAvailable ?? farm.cosechaDisponible ?? 0),
      plantAvailable: Number(farm.plantAvailable ?? farm.siembraDisponible ?? 0),
      waterAvailable: Number(farm.waterAvailable ?? farm.aguaDisponible ?? 0),
      working: Number(farm.working ?? farm.enProgreso ?? 0),
      totalLands: Number(farm.totalLands ?? farm.totalTierras ?? lands.length),

      hasWater: Boolean(
        farm.hasWater ??
          farm.tieneAgua ??
          Number(farm.waterAvailable ?? farm.aguaDisponible ?? 0) > 0
      ),
      hasWork: Boolean(
        farm.hasWork ??
          farm.tieneTrabajo ??
          Number(farm.available ?? farm.disponibles ?? 0) > 0
      ),
      withPack: Boolean(farm.withPack ?? farm.conPack),

      image: getFarmPreviewImage({ lands }),
      lands,
    };
  });
}

function normalizeLand(land) {
  const color = land.color || land.mainColor || land.statusColor || "red";
  const status = land.status || land.state || land.estado || "";

  const canHarvest = Boolean(
    land.canHarvest ??
      land.puedeCosechar ??
      status === "ready_to_harvest"
  );

  const canPlant = Boolean(
    land.canPlant ??
      land.puedeSembrar ??
      status === "ready_to_plant"
  );

  const resourceKind = getResourceKind(land);

  const canCollectEnergy =
    Boolean(
      land.canCollectEnergy ??
        land.puedeRecogerEnergia ??
        (status === "ready_to_collect_energy")
    ) ||
    (resourceKind === "energy" && Boolean(land.canCollectWater || status === "ready_to_collect_water"));

  const canCollectWater =
    resourceKind !== "energy" &&
    Boolean(
      land.canCollectWater ??
        land.puedeRecogerAgua ??
        status === "ready_to_collect_water"
    );

  const canCollectResource = Boolean(
    land.canCollectResource ??
      land.puedeRecogerRecurso ??
      status === "ready_to_collect_resource"
  );

  const available = Boolean(
    land.available ??
      land.disponible ??
      (canHarvest || canPlant || canCollectWater || canCollectEnergy || canCollectResource)
  );

  return {
    key: land.key || land.name || land.nombre || "",
    id: Number(land.id || land.landId || land.tierraId || 0),
    level: Number(land.level || land.nivel || 1),
    produce: land.produce || land.produccionItem || land.item || land.crop || land.cultivo || "",
    image: getLandImage(land),
    status,
    color,
    text: land.text || land.texto || getStatusInfo(color).label,
    available,
    canHarvest,
    canPlant,
    canCollectWater,
    canCollectEnergy,
    canCollectResource,
    tiempo: land.tiempo || "",
    habilitado: land.habilitado,
    construccion: Boolean(land.construccion),
  };
}

function getSpanishAction(action) {
  if (action === "harvest") return "cosechar";
  if (action === "plant") return "sembrar";
  if (action === "collect_water") return "recoger_agua";
  if (action === "collect_energy") return "recoger_energia";
  if (action === "collect_resource") return "recoger_recurso";
  return action;
}

function formatDate(value) {
  if (!value) return "";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return "";
  }
}

/* ===================== MAIN COMPONENT ===================== */

export default function CityFarmVisitFarms({
  openByDefault = false,
  npcMode = true,
  onUserUpdate,

  // Permite usar este componente como modal interno
  // sin mostrar el botón propio.
  showAccessButton = true,

  // Evento global para abrirlo desde otros archivos,
  // por ejemplo desde el menú inferior de Prueba.jsx.
  externalOpenEvent = "open-city-farms-panel",
}) {
  const [open, setOpen] = useState(openByDefault);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);

  const [farms, setFarms] = useState([]);
  const [state, setState] = useState(null);
  const [workerHistory, setWorkerHistory] = useState([]);
  const [ownerHistory, setOwnerHistory] = useState([]);

  const [selectedFarm, setSelectedFarm] = useState(null);
  const [selectedLand, setSelectedLand] = useState(null);
  const [notice, setNotice] = useState(null);
  const [tab, setTab] = useState("farms");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [packFilter, setPackFilter] = useState("all");
  const [sortBy, setSortBy] = useState("available");
  const [onlyAvailable, setOnlyAvailable] = useState(false);

  const tokenExists = useMemo(() => Boolean(getStoredToken()), []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function openFromExternalButton() {
      setOpen(true);
      setSelectedFarm(null);
      setSelectedLand(null);
      setTab("farms");
      setNotice(null);
    }

    window.addEventListener(externalOpenEvent, openFromExternalButton);

    return () => {
      window.removeEventListener(externalOpenEvent, openFromExternalButton);
    };
  }, [externalOpenEvent]);

  useEffect(() => {
    if (!open) return;

    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function loadAll() {
    setLoading(true);
    setNotice(null);

    try {
      // Solo estado + granjas controlan el spinner principal. Los historiales
      // son secundarios y no deben dejar toda la pantalla bloqueada si uno
      // de sus endpoints está lento.
      await Promise.allSettled([loadState(), loadFarms()]);
    } finally {
      setLoading(false);
    }

    Promise.allSettled([loadWorkerHistory(), loadOwnerHistory()]).catch(() => {});
  }

  async function loadState() {
    const result = await apiGetWithFallback("/state", "/estado");

    if (result.ok && result.data) {
      setState(result.data);
    } else {
      setNotice({
        type: "error",
        text: buildErrorMessage(result, "Could not load city state."),
      });
    }
  }

  async function loadFarms({ silent = false } = {}) {
    const result = await apiGetWithFallback("/farms", "/granjas");

    if (result.ok && result.data) {
      const normalized = normalizeFarms(result.data);
      setFarms(normalized);

      if (selectedFarm?.owner) {
        const updatedSelected = normalized.find((farm) => farm.owner === selectedFarm.owner);
        if (updatedSelected) {
          setSelectedFarm(updatedSelected);
        }
      }

      return normalized;
    }

    // IMPORTANTE: una caída/timeout temporal de /farms no debe borrar la lista
    // que el jugador ya estaba usando. Antes se hacía setFarms([]), por eso el
    // panel quedaba completamente verde después de varias tareas aunque las
    // acciones sí se hubieran registrado. Conservamos el último snapshot útil.
    if (!silent) {
      setNotice({
        type: "error",
        text: `${buildErrorMessage(result, "Could not refresh farms.")} The last farm list was kept so you can continue.`,
      });
    }

    return null;
  }

  async function loadWorkerHistory() {
    const result = await apiGetWithFallback("/worker-history", "/historial");

    if (result.ok && result.data) {
      setWorkerHistory(result.data.history || result.data.historial || []);
    }
  }

  async function loadOwnerHistory() {
    const result = await apiGetWithFallback("/owner-history", "/historial-propietario");

    if (result.ok && result.data) {
      setOwnerHistory(result.data.history || result.data.historial || []);
    }
  }

  function openFarm(farm) {
    setSelectedFarm(farm);

    const lands = (farm.lands || []).map(normalizeLand).filter((land) => land.id > 0);
    const bestLand =
      lands.find((land) => land.canCollectEnergy) ||
      lands.find((land) => land.canCollectWater) ||
      lands.find((land) => land.canHarvest) ||
      lands.find((land) => land.canPlant) ||
      lands[0] ||
      null;

    setSelectedLand(bestLand);
    setTab("lands");
  }

  function closeModal() {
    setOpen(false);
    setSelectedFarm(null);
    setSelectedLand(null);
    setNotice(null);
  }

  function resetFilters() {
    setSearch("");
    setStatusFilter("all");
    setPackFilter("all");
    setSortBy("available");
    setOnlyAvailable(false);
  }

  async function handleWork(action, land) {
    if (!selectedFarm?.owner) {
      setNotice({ type: "error", text: "Invalid farm owner." });
      return;
    }

    if (!land?.id) {
      setNotice({ type: "error", text: "Invalid land." });
      return;
    }

    setWorking(true);
    setNotice(null);

    try {
      const actionCandidates =
        action === "collect_energy"
          ? ["collect_energy", "collect_resource", "collect_water"]
          : action === "collect_resource"
          ? ["collect_resource", "collect_water"]
          : [action];

      let result = null;

      for (const actionToSend of actionCandidates) {
        const englishBody = {
          action: actionToSend,
          owner: selectedFarm.owner,
          landId: land.id,
          seeds: DEFAULT_SEEDS,
        };

        const spanishBody = {
          accion: getSpanishAction(actionToSend),
          propietario: selectedFarm.owner,
          tierraId: land.id,
          semillas: DEFAULT_SEEDS,
        };

        result = await apiPostWithFallback("/work", "/trabajar", englishBody);

        if (!result.ok && [400, 404, 422].includes(result.status)) {
          result = await apiPostWithFallback("/work", "/trabajar", spanishBody);
        }

        if (result.ok || ![400, 404, 422].includes(result.status)) {
          break;
        }
      }

      if (!result.ok) {
        setNotice({
          type: "error",
          text: buildErrorMessage(result, "The work action could not be completed."),
        });
        return;
      }

      const data = result.data || {};

      setNotice({
        type: "success",
        text:
          data.message ||
          data.mensaje ||
          `${LAND_ACTION_TEXT[action]?.title || "Work"} completed. Reward sent to your inventory.`,
      });

      if (data.user || data.usuario || data.worker) {
        onUserUpdate?.(data.user || data.usuario || data.worker);
      }

      // La respuesta de /work ya trae el estado actualizado de la granja.
      // Lo aplicamos de inmediato y NO bloqueamos el botón esperando /farms.
      // Esto elimina el "panel verde congelado" cuando Mongo tarda en reconstruir
      // el listado global después de varias tareas.
      const ownerSnapshot =
        data.ownerUpdatedFarm || data.ownerUpdated || data.farm || data.granjaActualizada || null;

      if (ownerSnapshot) {
        const freshSelected = normalizeFarms({ farms: [ownerSnapshot] })[0] || null;

        if (freshSelected?.owner) {
          setFarms((current) => {
            const list = Array.isArray(current) ? current : [];
            const index = list.findIndex((farm) => farm.owner === freshSelected.owner);

            if (index < 0) return [freshSelected, ...list];

            const next = [...list];
            next[index] = freshSelected;
            return next;
          });

          setSelectedFarm(freshSelected);

          const lands = (freshSelected.lands || [])
            .map(normalizeLand)
            .filter((item) => item.id > 0);

          const sameLand = lands.find((item) => item.id === land.id);
          setSelectedLand(sameLand || lands[0] || null);
        }
      }

      if (data.workerState) {
        setState((previous) => ({
          ...(previous || {}),
          actions: {
            ...(previous?.actions || {}),
            used: Number(data.workerState.actionsUsed ?? previous?.actions?.used ?? 0),
            available: Number(
              data.workerState.actionsAvailable ?? previous?.actions?.available ?? 0
            ),
          },
          earnedToday: {
            ...(previous?.earnedToday || {}),
            Water: Number(
              data.workerState.waterEarnedToday ?? previous?.earnedToday?.Water ?? 0
            ),
            Croquettes: Number(
              data.workerState.croquettesEarnedToday ??
                previous?.earnedToday?.Croquettes ??
                0
            ),
          },
        }));
      }

      // Sin espera: refrescos de consistencia en segundo plano. Si /farms está
      // lento, la UI conserva el snapshot anterior y el usuario no pierde la pantalla.
      Promise.allSettled([
        loadState(),
        loadFarms({ silent: true }),
        loadWorkerHistory(),
        loadOwnerHistory(),
      ]).catch(() => {});
    } finally {
      setWorking(false);
    }
  }

  const stats = useMemo(() => {
    const total = farms.length;
    const available = farms.filter((farm) => farm.available > 0).length;
    const water = farms.filter((farm) => farm.waterAvailable > 0).length;
    const harvest = farms.filter((farm) => farm.harvestAvailable > 0).length;
    const plant = farms.filter((farm) => farm.plantAvailable > 0).length;
    const progress = farms.filter((farm) => farm.working > 0 && farm.available <= 0).length;

    return {
      total,
      available,
      water,
      harvest,
      plant,
      progress,
    };
  }, [farms]);

  const filteredFarms = useMemo(() => {
    const query = search.trim().toLowerCase();

    let list = farms.filter((farm) => {
      const owner = String(farm.owner || "").toLowerCase();
      const email = String(farm.email || "").toLowerCase();

      if (query && !owner.includes(query) && !email.includes(query)) {
        return false;
      }

      if (onlyAvailable && farm.available <= 0) {
        return false;
      }

      if (packFilter === "with_pack" && !farm.withPack) {
        return false;
      }

      if (packFilter === "without_pack" && farm.withPack) {
        return false;
      }

      if (statusFilter === "available" && farm.available <= 0) {
        return false;
      }

      if (statusFilter === "water" && farm.waterAvailable <= 0) {
        return false;
      }

      if (statusFilter === "harvest" && farm.harvestAvailable <= 0) {
        return false;
      }

      if (statusFilter === "plant" && farm.plantAvailable <= 0) {
        return false;
      }

      if (statusFilter === "progress" && !(farm.working > 0 && farm.available <= 0)) {
        return false;
      }

      if (statusFilter === "empty" && farm.available > 0) {
        return false;
      }

      return true;
    });

    list = [...list].sort((a, b) => {
      if (sortBy === "available") {
        if (b.available !== a.available) return b.available - a.available;
        if (b.waterAvailable !== a.waterAvailable) return b.waterAvailable - a.waterAvailable;
        if (b.harvestAvailable !== a.harvestAvailable) return b.harvestAvailable - a.harvestAvailable;
        if (b.plantAvailable !== a.plantAvailable) return b.plantAvailable - a.plantAvailable;
        return String(a.owner || "").localeCompare(String(b.owner || ""));
      }

      if (sortBy === "water") {
        if (b.waterAvailable !== a.waterAvailable) return b.waterAvailable - a.waterAvailable;
        return b.available - a.available;
      }

      if (sortBy === "harvest") {
        if (b.harvestAvailable !== a.harvestAvailable) {
          return b.harvestAvailable - a.harvestAvailable;
        }
        return b.available - a.available;
      }

      if (sortBy === "plant") {
        if (b.plantAvailable !== a.plantAvailable) {
          return b.plantAvailable - a.plantAvailable;
        }
        return b.available - a.available;
      }

      if (sortBy === "lands") {
        if (b.totalLands !== a.totalLands) return b.totalLands - a.totalLands;
        return b.available - a.available;
      }

      if (sortBy === "name") {
        return String(a.owner || "").localeCompare(String(b.owner || ""));
      }

      return 0;
    });

    return list;
  }, [farms, search, statusFilter, packFilter, sortBy, onlyAvailable]);

  const AccessButton = (
    <button
      type="button"
      className={npcMode ? "cf-city-npc-button" : "cf-city-main-button"}
      onClick={() => setOpen(true)}
      title="Visit other farms"
    >
      {npcMode ? (
        <>
          <span className="cf-city-npc-icon">👩‍🌾</span>
          <span className="cf-city-npc-bubble">
            Visit farms
            <small>Get rewards for your dog</small>
          </span>
        </>
      ) : (
        <>
          <span>🏙️</span> Visit Farms
        </>
      )}
    </button>
  );

  const overlayContent = open ? (
    <div className="cf-city-overlay">
          <div className="cf-city-modal">
            <div className="cf-city-header">
              <div>
                <h2>🏙️ Visit Farms</h2>
                <p>Work on other farms and earn Water / Croquettes for your dog.</p>
              </div>

              <button type="button" className="cf-city-close" onClick={closeModal}>
                ✖
              </button>
            </div>

            <div className="cf-city-topbar">
              <div className="cf-city-stat">
                <span>💧 Water today</span>
                <strong>
                  {state?.earnedToday?.Water ??
                    state?.earnedToday?.water ??
                    state?.ganadoHoy?.Water ??
                    state?.ganadoHoy?.water ??
                    0}
                </strong>
              </div>

              <div className="cf-city-stat">
                <span>🐶 Croquettes today</span>
                <strong>
                  {state?.earnedToday?.Croquettes ??
                    state?.earnedToday?.croquettes ??
                    state?.ganadoHoy?.Croquettes ??
                    state?.ganadoHoy?.croquettes ??
                    0}
                </strong>
              </div>

              <div className="cf-city-stat">
                <span>✅ Actions</span>
                <strong>
                  {state?.actions?.available ?? state?.acciones?.disponibles ?? 0}/
                  {state?.actions?.max ?? state?.acciones?.max ?? 0}
                </strong>
              </div>

              <button
                type="button"
                className="cf-city-refresh"
                onClick={loadAll}
                disabled={loading}
              >
                {loading ? "Loading..." : "🔄 Refresh"}
              </button>
            </div>

            {!tokenExists && (
              <div className="cf-city-notice cf-city-notice-error">
                Session token was not found in cookies. Log in again before working.
              </div>
            )}

            {notice && (
              <div
                className={
                  notice.type === "success"
                    ? "cf-city-notice cf-city-notice-success"
                    : "cf-city-notice cf-city-notice-error"
                }
              >
                {notice.text}
              </div>
            )}

            <div className="cf-city-tabs">
              <button
                type="button"
                className={tab === "farms" ? "active" : ""}
                onClick={() => {
                  setTab("farms");
                  setSelectedFarm(null);
                  setSelectedLand(null);
                }}
              >
                🌍 Farms
              </button>

              <button
                type="button"
                className={tab === "worker" ? "active" : ""}
                onClick={() => setTab("worker")}
              >
                📜 My work
              </button>

              <button
                type="button"
                className={tab === "owner" ? "active" : ""}
                onClick={() => setTab("owner")}
              >
                🏡 My farm log
              </button>
            </div>

            <div className="cf-city-content">
              {tab === "farms" && !selectedFarm && (
                <>
                  <FarmStats stats={stats} />
                  <FarmFilters
                    search={search}
                    setSearch={setSearch}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    packFilter={packFilter}
                    setPackFilter={setPackFilter}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    onlyAvailable={onlyAvailable}
                    setOnlyAvailable={setOnlyAvailable}
                    resetFilters={resetFilters}
                  />

                  <FarmsGrid
                    farms={filteredFarms}
                    totalFarms={farms.length}
                    loading={loading}
                    onOpenFarm={openFarm}
                  />
                </>
              )}

              {tab === "lands" && selectedFarm && (
                <FarmDetails
                  farm={selectedFarm}
                  selectedLand={selectedLand}
                  setSelectedLand={setSelectedLand}
                  onBack={() => {
                    setSelectedFarm(null);
                    setSelectedLand(null);
                    setTab("farms");
                  }}
                  onWork={handleWork}
                  working={working}
                />
              )}

              {tab === "worker" && (
                <HistoryPanel
                  title="My worker history"
                  empty="You have not worked on other farms today."
                  history={workerHistory}
                />
              )}

              {tab === "owner" && (
                <HistoryPanel
                  title="My farm activity"
                  empty="Nobody has worked on your farm today."
                  history={ownerHistory}
                />
              )}
            </div>
          </div>
    </div>
  ) : null;

  return (
    <>
      <CityFarmVisitFarmsStyles />
      {showAccessButton && AccessButton}
      {overlayContent &&
        (typeof document !== "undefined"
          ? createPortal(overlayContent, document.body)
          : overlayContent)}
    </>
  );
}

/* ===================== SUB COMPONENTS ===================== */

function FarmStats({ stats }) {
  return (
    <div className="cf-city-summary">
      <div className="cf-city-summary-card">
        <span>🏡 Total farms</span>
        <strong>{stats.total}</strong>
      </div>

      <div className="cf-city-summary-card good">
        <span>✅ With actions</span>
        <strong>{stats.available}</strong>
      </div>

      <div className="cf-city-summary-card water">
        <span>💧 Water ready</span>
        <strong>{stats.water}</strong>
      </div>

      <div className="cf-city-summary-card harvest">
        <span>🌾 Harvest</span>
        <strong>{stats.harvest}</strong>
      </div>

      <div className="cf-city-summary-card plant">
        <span>🌱 Plant</span>
        <strong>{stats.plant}</strong>
      </div>

      <div className="cf-city-summary-card progress">
        <span>⏳ Progress</span>
        <strong>{stats.progress}</strong>
      </div>
    </div>
  );
}

function FarmFilters({
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  packFilter,
  setPackFilter,
  sortBy,
  setSortBy,
  onlyAvailable,
  setOnlyAvailable,
  resetFilters,
}) {
  return (
    <div className="cf-city-filter-box">
      <div className="cf-city-search">
        <span>🔎</span>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search farm owner..."
        />
      </div>

      <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
        <option value="all">All statuses</option>
        <option value="available">Any action available</option>
        <option value="water">Water ready</option>
        <option value="harvest">Ready to harvest</option>
        <option value="plant">Ready to plant</option>
        <option value="progress">In progress</option>
        <option value="empty">No actions</option>
      </select>

      <select value={packFilter} onChange={(event) => setPackFilter(event.target.value)}>
        <option value="all">All packs</option>
        <option value="with_pack">With starter pack</option>
        <option value="without_pack">Without starter pack</option>
      </select>

      <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
        <option value="available">Sort: Best actions</option>
        <option value="water">Sort: Water first</option>
        <option value="harvest">Sort: Harvest first</option>
        <option value="plant">Sort: Plant first</option>
        <option value="lands">Sort: More lands</option>
        <option value="name">Sort: Name</option>
      </select>

      <label className="cf-city-check">
        <input
          type="checkbox"
          checked={onlyAvailable}
          onChange={(event) => setOnlyAvailable(event.target.checked)}
        />
        <span>Only available</span>
      </label>

      <button type="button" className="cf-city-clear" onClick={resetFilters}>
        Reset
      </button>
    </div>
  );
}

function FarmsGrid({ farms, totalFarms, loading, onOpenFarm }) {
  if (loading) {
    return (
      <div className="cf-city-loading">
        <div>
          <div className="cf-city-spinner" />
          <p>Loading farms...</p>
        </div>
      </div>
    );
  }

  if (!farms.length) {
    return (
      <div className="cf-city-empty">
        <h3>🌙 No farms match your filters</h3>
        <p>
          {totalFarms > 0
            ? "Try changing the filters or search."
            : "Come back later. Other farms may have work ready soon."}
        </p>
      </div>
    );
  }

  return (
    <div className="cf-city-grid">
      {farms.map((farm) => {
        const status = getStatusInfo(farm.color);

        return (
          <button
            type="button"
            key={farm.owner}
            className="cf-city-farm-card"
            onClick={() => onOpenFarm(farm)}
          >
            <div className="cf-city-farm-image-wrap">
              <img
                src={farm.image || FARM_IMAGE}
                alt={`${farm.owner} farm`}
                className="cf-city-farm-image"
                onError={(event) => {
                  event.currentTarget.src = FARM_IMAGE;
                }}
              />
              <span className={`cf-city-status ${status.className}`}>
                {status.emoji} {status.label}
              </span>
            </div>

            <div className="cf-city-farm-info">
              <h3>{shortName(farm.owner)}</h3>

              <div className="cf-city-farm-row">
                <span>Available actions</span>
                <strong>{farm.available}</strong>
              </div>

              <div className="cf-city-mini-actions">
                <small>💧 {farm.waterAvailable}</small>
                <small>🌾 {farm.harvestAvailable}</small>
                <small>🌱 {farm.plantAvailable}</small>
                <small>⏳ {farm.working}</small>
              </div>

              <div className="cf-city-farm-row">
                <span>Total lands</span>
                <strong>{farm.totalLands}</strong>
              </div>

              <div className="cf-city-farm-row">
                <span>Starter pack</span>
                <strong>{farm.withPack ? "Yes" : "No"}</strong>
              </div>

              <div className="cf-city-tags">
                {farm.waterAvailable > 0 && <small>💧 Water</small>}
                {farm.harvestAvailable > 0 && <small>🌾 Harvest</small>}
                {farm.plantAvailable > 0 && <small>🌱 Plant</small>}
                {farm.available <= 0 && farm.working > 0 && <small>⏳ In progress</small>}
                {farm.available <= 0 && farm.working <= 0 && <small>⛔ No actions</small>}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function FarmDetails({
  farm,
  selectedLand,
  setSelectedLand,
  onBack,
  onWork,
  working,
}) {
  const [landFilter, setLandFilter] = useState("all");

  const lands = useMemo(() => {
    return (farm.lands || []).map(normalizeLand).filter((land) => land.id > 0);
  }, [farm]);

  const filteredLands = useMemo(() => {
    return lands.filter((land) => {
      if (landFilter === "all") return true;
      if (landFilter === "available") return land.available;
      if (landFilter === "water") return land.canCollectWater;
      if (landFilter === "harvest") return land.canHarvest;
      if (landFilter === "plant") return land.canPlant;
      if (landFilter === "progress") return land.status === "working";
      if (landFilter === "empty") return !land.available;
      return true;
    });
  }, [lands, landFilter]);

  const availableActions = getAvailableLandActions(selectedLand);

  return (
    <div className="cf-city-details">
      <div className="cf-city-details-header">
        <button type="button" className="cf-city-back" onClick={onBack}>
          ← Back
        </button>

        <div>
          <h3>🏡 {farm.owner}</h3>
          <p>Select a land and work as a farm helper.</p>
        </div>
      </div>

      <div className="cf-city-land-tools">
        <button
          type="button"
          className={landFilter === "all" ? "active" : ""}
          onClick={() => setLandFilter("all")}
        >
          All
        </button>
        <button
          type="button"
          className={landFilter === "available" ? "active" : ""}
          onClick={() => setLandFilter("available")}
        >
          ✅ Available
        </button>
        <button
          type="button"
          className={landFilter === "water" ? "active" : ""}
          onClick={() => setLandFilter("water")}
        >
          💧 Water
        </button>
        <button
          type="button"
          className={landFilter === "harvest" ? "active" : ""}
          onClick={() => setLandFilter("harvest")}
        >
          🌾 Harvest
        </button>
        <button
          type="button"
          className={landFilter === "plant" ? "active" : ""}
          onClick={() => setLandFilter("plant")}
        >
          🌱 Plant
        </button>
      </div>

      <div className="cf-city-land-layout">
        <div className="cf-city-land-list">
          {!filteredLands.length ? (
            <div className="cf-city-empty small">
              <p>No lands match this filter.</p>
            </div>
          ) : (
            filteredLands.map((land) => {
              const status = getStatusInfo(land.color);
              const active = selectedLand?.id === land.id;

              return (
                <button
                  type="button"
                  key={`${land.key}-${land.id}`}
                  className={active ? "cf-city-land active" : "cf-city-land"}
                  onClick={() => setSelectedLand(land)}
                >
                  <span className="cf-city-land-thumb-wrap">
                    <img
                      src={land.image || FARM_IMAGE}
                      alt={`${land.produce || "Land"} #${land.id}`}
                      className="cf-city-land-thumb"
                      onError={(event) => {
                        event.currentTarget.src = FARM_IMAGE;
                      }}
                    />
                    <i className={`cf-city-land-status-dot ${status.className}`} />
                  </span>

                  <div className="cf-city-land-text">
                    <strong>
                      <span>{getLandEmoji(land)}</span> {land.produce || "Land"} #{land.id}
                    </strong>
                    <small>
                      LVL-{land.level} · {land.text}
                    </small>
                  </div>

                  {land.available && <em>READY</em>}
                </button>
              );
            })
          )}
        </div>

        <div className="cf-city-work-panel">
          {!selectedLand ? (
            <div className="cf-city-empty small">
              <h3>👈 Select a land</h3>
              <p>Choose a land to see available work actions.</p>
            </div>
          ) : (
            <>
              <div className="cf-city-selected-land">
                <img
                  src={selectedLand.image || FARM_IMAGE}
                  alt={`${selectedLand.produce || "Land"} #${selectedLand.id}`}
                  onError={(event) => {
                    event.currentTarget.src = FARM_IMAGE;
                  }}
                />

                <div>
                  <h3>
                    {selectedLand.produce || "Land"} #{selectedLand.id}
                  </h3>
                  <p>
                    LVL-{selectedLand.level} · {selectedLand.text}
                  </p>

                  <div className="cf-city-selected-tags">
                    {selectedLand.canCollectEnergy && <small>⚡ Energy ready</small>}
                    {selectedLand.canCollectResource && !selectedLand.canCollectEnergy && <small>📦 Resource ready</small>}
                    {selectedLand.canCollectWater && !selectedLand.canCollectEnergy && !selectedLand.canCollectResource && <small>💧 Water ready</small>}
                    {selectedLand.canHarvest && <small>🌾 Ready to harvest</small>}
                    {selectedLand.canPlant && <small>🌱 Ready to plant</small>}
                    {!selectedLand.available && <small>⏳ No action now</small>}
                  </div>
                </div>
              </div>

              <div className="cf-city-action-list">
                {availableActions.length ? (
                  availableActions.map((action) => (
                    <WorkActionButton
                      key={action}
                      action={action}
                      disabled={working || !selectedLand.available}
                      onClick={() => onWork(action, selectedLand)}
                    />
                  ))
                ) : (
                  <div className="cf-city-empty small cf-city-action-empty">
                    <h3>⏳ No action available</h3>
                    <p>This land exists, but it is not ready for helper work right now.</p>
                  </div>
                )}
              </div>

              <div className="cf-city-rule-box">
                <strong>Economy rule:</strong>
                <span>
                  The owner receives production. Owner resources are used for planting.
                  The worker receives only a controlled Water/Croquettes reward.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function WorkActionButton({ action, disabled, onClick }) {
  const info = LAND_ACTION_TEXT[action];

  if (!info) return null;

  return (
    <button
      type="button"
      className="cf-city-work-button"
      disabled={disabled}
      onClick={onClick}
    >
      <span className="cf-city-work-emoji">{info.emoji}</span>
      <span>
        <strong>{info.title}</strong>
        <small>{info.description}</small>
      </span>
    </button>
  );
}

function HistoryPanel({ title, empty, history }) {
  const list = Array.isArray(history) ? history : [];

  return (
    <div className="cf-city-history">
      <h3>{title}</h3>

      {!list.length ? (
        <div className="cf-city-empty small">
          <p>{empty}</p>
        </div>
      ) : (
        <div className="cf-city-history-list">
          {list.map((item, index) => (
            <div
              className="cf-city-history-item"
              key={`${item.fecha || item.date || index}-${index}`}
            >
              <div>
                <strong>
                  {item.emoji || "✅"}{" "}
                  {item.actionName ||
                    item.accionNombre ||
                    item.action ||
                    item.accion ||
                    "Action"}
                </strong>
                <small>
                  {item.farm ||
                    item.granja ||
                    item.owner ||
                    item.propietario ||
                    item.visitor ||
                    item.visitante ||
                    ""}
                </small>
              </div>

              <div className="cf-city-history-reward">
                <span>
                  💧{" "}
                  {item.water ??
                    item.Water ??
                    item?.reward?.Water ??
                    item?.workerReward?.Water ??
                    item?.recompensaVisitante?.Water ??
                    0}
                </span>

                <span>
                  🐶{" "}
                  {item.croquettes ??
                    item.Croquettes ??
                    item?.reward?.Croquettes ??
                    item?.workerReward?.Croquettes ??
                    item?.recompensaVisitante?.Croquettes ??
                    0}
                </span>
              </div>

              <small className="cf-city-history-date">
                {formatDate(item.date || item.fecha)}
              </small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ===================== STYLES INSIDE THE SAME FILE ===================== */

function CityFarmVisitFarmsStyles() {
  return (
    <style>{`
      .cf-city-main-button,
      .cf-city-npc-button {
        border: 0;
        cursor: pointer;
        font-family: inherit;
      }

      .cf-city-main-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px 18px;
        border-radius: 14px;
        background: linear-gradient(180deg, #24e45a 0%, #148a32 100%);
        color: #ffffff;
        font-size: 16px;
        font-weight: 900;
        text-shadow: 1px 2px 0 rgba(0,0,0,.35);
        box-shadow: 0 5px 0 #07531c, 0 10px 18px rgba(0,0,0,.35);
      }

      .cf-city-main-button:hover {
        transform: translateY(-1px);
        filter: brightness(1.08);
      }

      .cf-city-npc-button {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: transparent;
        padding: 0;
      }

      .cf-city-npc-icon {
        width: 54px;
        height: 54px;
        display: grid;
        place-items: center;
        border-radius: 999px;
        background: rgba(0, 0, 0, .45);
        border: 3px solid #55ff75;
        box-shadow: 0 0 16px rgba(85,255,117,.6);
        font-size: 30px;
        animation: cfCityPulse 1.5s infinite;
      }

      .cf-city-npc-bubble {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 9px 12px;
        border-radius: 12px;
        background: rgba(0,0,0,.72);
        border: 2px solid #ffc857;
        color: #fff;
        font-weight: 900;
        text-transform: uppercase;
        text-shadow: 1px 2px 0 #000;
        box-shadow: 0 4px 0 rgba(0,0,0,.45);
      }

      .cf-city-npc-bubble small {
        margin-top: 2px;
        font-size: 10px;
        color: #8cff9f;
        text-transform: none;
      }

      .cf-city-overlay {
        position: fixed;
        inset: 0;
        z-index: 2147483000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: clamp(10px, 1.2vw, 22px);
        background: rgba(0,0,0,.72);
        backdrop-filter: blur(4px);
        overflow: hidden;
        box-sizing: border-box;
      }

      .cf-city-modal {
        width: min(96vw, 1680px);
        height: min(88vh, 880px);
        max-width: 1680px;
        max-height: 88vh;
        min-width: min(96vw, 980px);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border-radius: 22px;
        box-sizing: border-box;
        transform: none !important;
        animation: none !important;
        background:
          linear-gradient(180deg, rgba(42, 113, 45, .96), rgba(23, 77, 32, .98)),
          repeating-linear-gradient(45deg, rgba(255,255,255,.04) 0 8px, transparent 8px 16px);
        border: 5px solid #2b1a10;
        box-shadow:
          0 0 0 5px #e38d2c,
          0 0 0 8px #44210d,
          0 25px 80px rgba(0,0,0,.65);
        color: #fff;
      }

      .cf-city-header {
        flex: 0 0 auto;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        padding: 14px 20px;
        background: rgba(0,0,0,.38);
        border-bottom: 4px solid rgba(0,0,0,.35);
      }

      .cf-city-header h2 {
        margin: 0;
        font-size: clamp(20px, 2.5vw, 32px);
        font-weight: 1000;
        text-transform: uppercase;
        letter-spacing: .5px;
        color: #ffe28a;
        text-shadow: 2px 3px 0 #000;
      }

      .cf-city-header p {
        margin: 5px 0 0;
        color: #d8ffd7;
        font-weight: 700;
      }

      .cf-city-close {
        width: 44px;
        height: 44px;
        border: 0;
        border-radius: 12px;
        cursor: pointer;
        background: #21120c;
        color: #fff;
        font-size: 20px;
        font-weight: 900;
        box-shadow: inset 0 0 0 2px #ffcc63;
      }

      .cf-city-topbar {
        flex: 0 0 auto;
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 12px;
        padding: 14px 18px;
        background: rgba(0,0,0,.22);
        border-bottom: 2px solid rgba(255,255,255,.08);
      }

      .cf-city-stat {
        padding: 10px 12px;
        border-radius: 14px;
        background: rgba(0,0,0,.42);
        border: 2px solid rgba(255,255,255,.08);
      }

      .cf-city-stat span {
        display: block;
        font-size: 11px;
        text-transform: uppercase;
        color: #b9ffbc;
        font-weight: 900;
      }

      .cf-city-stat strong {
        display: block;
        margin-top: 3px;
        font-size: 20px;
        color: #fff;
        text-shadow: 1px 2px 0 #000;
      }

      .cf-city-refresh {
        min-height: 58px;
        padding: 0 16px;
        border: 0;
        border-radius: 14px;
        background: linear-gradient(180deg, #ffd86a, #d88422);
        color: #251309;
        font-weight: 1000;
        cursor: pointer;
        box-shadow: 0 4px 0 rgba(0,0,0,.35);
      }

      .cf-city-tabs {
        flex: 0 0 auto;
        display: flex;
        gap: 8px;
        padding: 10px 16px;
        background: rgba(0,0,0,.14);
        overflow-x: auto;
      }

      .cf-city-tabs button {
        border: 0;
        cursor: pointer;
        white-space: nowrap;
        padding: 10px 14px;
        border-radius: 12px;
        background: rgba(0,0,0,.45);
        color: #c7ffd1;
        font-weight: 1000;
        box-shadow: inset 0 0 0 2px rgba(255,255,255,.08);
      }

      .cf-city-tabs button.active {
        background: linear-gradient(180deg, #30e760, #12862f);
        color: #fff;
        text-shadow: 1px 2px 0 #000;
      }

      .cf-city-content {
        flex: 1 1 auto;
        min-height: 0;
        padding: 18px;
        overflow-y: auto;
        overflow-x: hidden;
        box-sizing: border-box;
      }

      .cf-city-summary {
        display: grid;
        grid-template-columns: repeat(6, minmax(120px, 1fr));
        gap: 10px;
        margin-bottom: 12px;
      }

      .cf-city-summary-card {
        padding: 10px 12px;
        border-radius: 14px;
        background: rgba(0,0,0,.36);
        box-shadow: inset 0 0 0 2px rgba(255,255,255,.08);
      }

      .cf-city-summary-card span {
        display: block;
        color: #c6ffd0;
        font-size: 11px;
        font-weight: 900;
        text-transform: uppercase;
      }

      .cf-city-summary-card strong {
        display: block;
        margin-top: 4px;
        font-size: 22px;
        color: #ffe28a;
        text-shadow: 1px 2px 0 #000;
      }

      .cf-city-summary-card.good strong { color: #65ff81; }
      .cf-city-summary-card.water strong { color: #66eaff; }
      .cf-city-summary-card.harvest strong { color: #72ff85; }
      .cf-city-summary-card.plant strong { color: #70a7ff; }
      .cf-city-summary-card.progress strong { color: #ffd06a; }

      .cf-city-filter-box {
        display: grid;
        grid-template-columns: minmax(220px, 1.4fr) repeat(3, minmax(150px, 1fr)) auto auto;
        gap: 10px;
        margin-bottom: 16px;
        padding: 12px;
        border-radius: 16px;
        background: rgba(0,0,0,.28);
        box-shadow: inset 0 0 0 2px rgba(255,255,255,.07);
      }

      .cf-city-search {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 0 10px;
        border-radius: 12px;
        background: rgba(0,0,0,.42);
        box-shadow: inset 0 0 0 2px rgba(255,255,255,.08);
      }

      .cf-city-search input {
        width: 100%;
        min-height: 42px;
        border: 0;
        outline: none;
        background: transparent;
        color: #fff;
        font-weight: 900;
      }

      .cf-city-search input::placeholder {
        color: rgba(255,255,255,.65);
      }

      .cf-city-filter-box select {
        min-height: 42px;
        border: 0;
        border-radius: 12px;
        padding: 0 10px;
        background: rgba(0,0,0,.45);
        color: #fff;
        font-weight: 900;
        box-shadow: inset 0 0 0 2px rgba(255,255,255,.08);
      }

      .cf-city-check {
        min-height: 42px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        padding: 0 10px;
        border-radius: 12px;
        background: rgba(0,0,0,.42);
        color: #d7ffd7;
        font-weight: 900;
        white-space: nowrap;
      }

      .cf-city-clear {
        min-height: 42px;
        border: 0;
        border-radius: 12px;
        padding: 0 14px;
        cursor: pointer;
        background: linear-gradient(180deg, #ffdf73, #d48623);
        color: #241409;
        font-weight: 1000;
      }

      .cf-city-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
        gap: 18px;
        align-items: stretch;
      }

      .cf-city-farm-card {
        overflow: hidden;
        text-align: left;
        border: 0;
        cursor: pointer;
        border-radius: 18px;
        min-width: 0;
        display: flex;
        flex-direction: column;
        height: 100%;
        background: rgba(0,0,0,.46);
        color: #fff;
        box-shadow:
          inset 0 0 0 3px rgba(255,255,255,.08),
          0 8px 0 rgba(0,0,0,.35);
        transition: transform .15s ease, filter .15s ease;
      }

      .cf-city-farm-card:hover {
        transform: translateY(-3px);
        filter: brightness(1.08);
      }

      .cf-city-farm-image-wrap {
        position: relative;
        min-height: clamp(140px, 16vh, 210px);
        display: grid;
        place-items: center;
        background:
          radial-gradient(circle at 50% 50%, rgba(93, 255, 91, .25), transparent 55%),
          repeating-linear-gradient(45deg, #258c31 0 10px, #1d7b2a 10px 20px);
      }

      .cf-city-farm-image {
        width: min(86%, 240px);
        max-height: clamp(120px, 15vh, 190px);
        object-fit: contain;
        image-rendering: auto;
        filter: drop-shadow(0 10px 8px rgba(0,0,0,.45));
      }

      .cf-city-status {
        position: absolute;
        top: 10px;
        left: 10px;
        max-width: calc(100% - 20px);
        padding: 6px 8px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 1000;
        text-transform: uppercase;
        color: #fff;
        text-shadow: 1px 1px 0 #000;
        box-shadow: 0 3px 0 rgba(0,0,0,.35);
      }

      .cf-city-status-blue,
      .cf-city-dot.cf-city-status-blue,
      .cf-city-land-status-dot.cf-city-status-blue { background: #168bff; }

      .cf-city-status-green,
      .cf-city-dot.cf-city-status-green,
      .cf-city-land-status-dot.cf-city-status-green { background: #17be42; }

      .cf-city-status-yellow,
      .cf-city-dot.cf-city-status-yellow,
      .cf-city-land-status-dot.cf-city-status-yellow { background: #d99216; }

      .cf-city-status-purple,
      .cf-city-dot.cf-city-status-purple,
      .cf-city-land-status-dot.cf-city-status-purple { background: #9b4dff; }

      .cf-city-status-red,
      .cf-city-dot.cf-city-status-red,
      .cf-city-land-status-dot.cf-city-status-red { background: #d93030; }

      .cf-city-status-gray,
      .cf-city-dot.cf-city-status-gray,
      .cf-city-land-status-dot.cf-city-status-gray { background: #777; }

      .cf-city-status-cyan,
      .cf-city-dot.cf-city-status-cyan,
      .cf-city-land-status-dot.cf-city-status-cyan { background: #14b8d4; }

      .cf-city-farm-info {
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        flex: 1 1 auto;
      }

      .cf-city-farm-info h3 {
        margin: 0 0 4px;
        font-size: 18px;
        color: #ffe28a;
        text-shadow: 1px 2px 0 #000;
      }

      .cf-city-farm-row {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        padding: 4px 0;
        border-top: 1px solid rgba(255,255,255,.08);
        color: #d7ffd7;
        font-size: 13px;
        font-weight: 800;
      }

      .cf-city-mini-actions {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 6px;
        margin: 2px 0 6px;
      }

      .cf-city-mini-actions small {
        text-align: center;
        padding: 6px 4px;
        border-radius: 10px;
        background: rgba(255,255,255,.11);
        font-weight: 1000;
      }

      .cf-city-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: auto;
      }

      .cf-city-tags small,
      .cf-city-selected-tags small {
        padding: 5px 8px;
        border-radius: 999px;
        background: rgba(255,255,255,.12);
        font-weight: 900;
      }

      .cf-city-details {
        height: 100%;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }

      .cf-city-details-header {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 12px;
      }

      .cf-city-details-header h3 {
        margin: 0;
        color: #ffe28a;
        text-shadow: 1px 2px 0 #000;
        font-size: 24px;
      }

      .cf-city-details-header p {
        margin: 3px 0 0;
        color: #d7ffd7;
        font-weight: 700;
      }

      .cf-city-back {
        border: 0;
        border-radius: 12px;
        padding: 10px 12px;
        background: rgba(0,0,0,.55);
        color: #fff;
        cursor: pointer;
        font-weight: 1000;
      }

      .cf-city-land-tools {
        flex: 0 0 auto;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 12px;
      }

      .cf-city-land-tools button {
        border: 0;
        border-radius: 999px;
        padding: 8px 12px;
        cursor: pointer;
        background: rgba(0,0,0,.45);
        color: #d7ffd7;
        font-weight: 1000;
        box-shadow: inset 0 0 0 2px rgba(255,255,255,.08);
      }

      .cf-city-land-tools button.active {
        background: linear-gradient(180deg, #30e760, #12862f);
        color: #fff;
        text-shadow: 1px 2px 0 #000;
      }

      .cf-city-land-layout {
        flex: 1 1 auto;
        min-height: 0;
        display: grid;
        grid-template-columns: minmax(320px, 34%) minmax(520px, 1fr);
        gap: 18px;
      }

      .cf-city-land-list {
        display: grid;
        align-content: start;
        gap: 8px;
        min-height: 0;
        max-height: 100%;
        overflow-y: auto;
        padding-right: 6px;
      }

      .cf-city-land {
        position: relative;
        display: flex;
        gap: 10px;
        align-items: center;
        text-align: left;
        border: 0;
        border-radius: 14px;
        padding: 9px 10px;
        background: rgba(0,0,0,.45);
        color: #fff;
        cursor: pointer;
        box-shadow: inset 0 0 0 2px rgba(255,255,255,.08);
      }

      .cf-city-land.active {
        background: rgba(39, 211, 78, .5);
        box-shadow: inset 0 0 0 2px #ffdb6e;
      }

      .cf-city-land em {
        margin-left: auto;
        font-style: normal;
        font-size: 10px;
        padding: 4px 6px;
        border-radius: 999px;
        background: #ffe28a;
        color: #241409;
        font-weight: 1000;
      }

      .cf-city-land-thumb-wrap {
        position: relative;
        flex: 0 0 auto;
        width: 52px;
        height: 52px;
        display: grid;
        place-items: center;
        border-radius: 12px;
        background:
          radial-gradient(circle at 50% 40%, rgba(255,255,255,.16), transparent 60%),
          rgba(255,255,255,.08);
        box-shadow: inset 0 0 0 2px rgba(255,255,255,.1);
        overflow: hidden;
      }

      .cf-city-land-thumb {
        width: 48px;
        height: 48px;
        object-fit: contain;
        filter: drop-shadow(0 4px 4px rgba(0,0,0,.45));
      }

      .cf-city-land-status-dot {
        position: absolute;
        right: 4px;
        bottom: 4px;
        width: 12px;
        height: 12px;
        border-radius: 999px;
        box-shadow: 0 0 8px rgba(255,255,255,.35), 0 0 8px currentColor;
        border: 2px solid rgba(0,0,0,.65);
      }

      .cf-city-land-text {
        min-width: 0;
      }

      .cf-city-dot {
        flex: 0 0 auto;
        width: 16px;
        height: 16px;
        border-radius: 999px;
        box-shadow: 0 0 10px currentColor;
      }

      .cf-city-land strong {
        display: block;
        color: #fff;
        text-shadow: 1px 1px 0 #000;
      }

      .cf-city-land small {
        display: block;
        margin-top: 2px;
        color: #c6ffd0;
        font-weight: 700;
      }

      .cf-city-work-panel {
        min-height: 0;
        height: 100%;
        overflow-y: auto;
        border-radius: 18px;
        padding: 18px;
        background: rgba(0,0,0,.4);
        box-shadow: inset 0 0 0 3px rgba(255,255,255,.07);
      }

      .cf-city-selected-land {
        display: flex;
        align-items: center;
        gap: 18px;
        margin-bottom: 18px;
      }

      .cf-city-selected-land img {
        width: clamp(140px, 14vw, 220px);
        height: clamp(105px, 11vw, 160px);
        object-fit: contain;
        border-radius: 14px;
        background: rgba(255,255,255,.08);
      }

      .cf-city-selected-land h3 {
        margin: 0;
        color: #ffe28a;
        font-size: clamp(22px, 2.2vw, 34px);
        text-shadow: 1px 2px 0 #000;
      }

      .cf-city-selected-land p {
        margin: 4px 0 0;
        color: #d7ffd7;
        font-weight: 800;
      }

      .cf-city-selected-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 10px;
      }

      .cf-city-action-list {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 12px;
      }

      .cf-city-work-button {
        min-height: 92px;
        display: flex;
        align-items: center;
        gap: 12px;
        text-align: left;
        border: 0;
        border-radius: 16px;
        padding: 14px 16px;
        cursor: pointer;
        background: linear-gradient(180deg, #ffe076, #d99022);
        color: #241409;
        box-shadow: 0 5px 0 rgba(0,0,0,.45);
        transition: transform .15s ease, filter .15s ease;
      }

      .cf-city-work-button:not(:disabled):hover {
        transform: translateY(-2px);
        filter: brightness(1.06);
      }

      .cf-city-action-empty {
        grid-column: 1 / -1;
      }

      .cf-city-work-button:disabled {
        opacity: .55;
        cursor: not-allowed;
        filter: grayscale(.3);
      }

      .cf-city-work-emoji {
        flex: 0 0 auto;
        width: 48px;
        height: 48px;
        display: grid;
        place-items: center;
        border-radius: 14px;
        background: rgba(255,255,255,.28);
        font-size: 34px;
      }

      .cf-city-work-button strong {
        display: block;
        font-size: clamp(18px, 1.7vw, 26px);
        font-weight: 1000;
      }

      .cf-city-work-button small {
        display: block;
        margin-top: 5px;
        font-weight: 800;
        line-height: 1.3;
      }

      .cf-city-rule-box {
        margin-top: 16px;
        display: grid;
        gap: 4px;
        padding: 12px;
        border-radius: 14px;
        background: rgba(0,0,0,.42);
        color: #d7ffd7;
        box-shadow: inset 0 0 0 2px rgba(255,255,255,.08);
      }

      .cf-city-rule-box strong { color: #ffe28a; }

      .cf-city-notice {
        flex: 0 0 auto;
        margin: 12px 16px 0;
        padding: 10px 12px;
        border-radius: 12px;
        font-weight: 900;
      }

      .cf-city-notice-success {
        background: rgba(27, 203, 70, .22);
        border: 2px solid #3cff70;
        color: #d7ffd7;
      }

      .cf-city-notice-error {
        background: rgba(212, 35, 35, .22);
        border: 2px solid #ff6464;
        color: #ffe1e1;
      }

      .cf-city-loading,
      .cf-city-empty {
        min-height: 260px;
        display: grid;
        place-items: center;
        text-align: center;
        padding: 20px;
        border-radius: 18px;
        background: rgba(0,0,0,.34);
        color: #d7ffd7;
      }

      .cf-city-empty.small { min-height: 160px; }

      .cf-city-empty h3 {
        margin: 0 0 6px;
        color: #ffe28a;
        text-shadow: 1px 2px 0 #000;
      }

      .cf-city-spinner {
        width: 48px;
        height: 48px;
        margin: 0 auto 10px;
        border-radius: 999px;
        border: 5px solid rgba(255,255,255,.25);
        border-top-color: #ffe28a;
        animation: cfCitySpin .8s linear infinite;
      }

      .cf-city-history h3 {
        margin: 0 0 14px;
        color: #ffe28a;
        text-shadow: 1px 2px 0 #000;
      }

      .cf-city-history-list {
        display: grid;
        gap: 10px;
      }

      .cf-city-history-item {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 8px 12px;
        align-items: center;
        padding: 12px;
        border-radius: 14px;
        background: rgba(0,0,0,.42);
        box-shadow: inset 0 0 0 2px rgba(255,255,255,.08);
      }

      .cf-city-history-item strong {
        color: #fff;
        text-shadow: 1px 1px 0 #000;
      }

      .cf-city-history-item small {
        display: block;
        margin-top: 2px;
        color: #c6ffd0;
      }

      .cf-city-history-reward {
        display: flex;
        gap: 8px;
        font-weight: 1000;
      }

      .cf-city-history-reward span {
        padding: 6px 8px;
        border-radius: 999px;
        background: rgba(255,255,255,.12);
      }

      .cf-city-history-date {
        grid-column: 1 / -1;
        opacity: .8;
      }

      @keyframes cfCitySpin {
        to { transform: rotate(360deg); }
      }

      @keyframes cfCityPulse {
        0%, 100% {
          transform: translateY(0);
          box-shadow: 0 0 14px rgba(85,255,117,.55);
        }
        50% {
          transform: translateY(-3px);
          box-shadow: 0 0 24px rgba(85,255,117,.95);
        }
      }

      @media (max-width: 1180px) {
        .cf-city-summary {
          grid-template-columns: repeat(3, 1fr);
        }

        .cf-city-filter-box {
          grid-template-columns: 1fr 1fr 1fr;
        }

        .cf-city-search {
          grid-column: 1 / -1;
        }
      }

      @media (max-width: 1024px) {
        .cf-city-modal {
          width: 98vw;
          height: 92vh;
          min-width: 0;
          max-height: 92vh;
        }

        .cf-city-topbar {
          grid-template-columns: repeat(2, minmax(130px, 1fr));
        }

        .cf-city-refresh {
          min-height: 52px;
        }

        .cf-city-land-layout {
          grid-template-columns: minmax(260px, 42%) 1fr;
        }

        .cf-city-action-list {
          grid-template-columns: 1fr;
        }

        .cf-city-work-button {
          min-height: 88px;
        }
      }

      @media (max-width: 760px) {
        .cf-city-overlay {
          padding: 0;
          align-items: stretch;
          justify-content: stretch;
        }

        .cf-city-modal {
          width: 100vw;
          height: 100dvh;
          max-width: 100vw;
          max-height: 100dvh;
          border-radius: 0;
          border-left: 0;
          border-right: 0;
        }

        .cf-city-header {
          padding: 12px;
        }

        .cf-city-header h2 {
          font-size: 20px;
        }

        .cf-city-header p {
          font-size: 12px;
        }

        .cf-city-close {
          width: 40px;
          height: 40px;
        }

        .cf-city-topbar {
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          padding: 10px;
        }

        .cf-city-stat {
          padding: 8px 10px;
        }

        .cf-city-stat strong {
          font-size: 16px;
        }

        .cf-city-refresh {
          grid-column: 1 / -1;
          min-height: 44px;
        }

        .cf-city-tabs {
          padding: 8px 10px;
        }

        .cf-city-tabs button {
          padding: 9px 12px;
          font-size: 13px;
        }

        .cf-city-content {
          padding: 10px;
        }

        .cf-city-summary {
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
        }

        .cf-city-filter-box {
          grid-template-columns: 1fr;
          gap: 8px;
          padding: 10px;
        }

        .cf-city-search {
          grid-column: auto;
        }

        .cf-city-grid {
          grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
          gap: 10px;
        }

        .cf-city-farm-image-wrap {
          min-height: 115px;
        }

        .cf-city-farm-image {
          max-height: 105px;
        }

        .cf-city-land-layout {
          display: flex;
          flex-direction: column;
          gap: 10px;
          min-height: 0;
        }

        .cf-city-land-list {
          max-height: 34dvh;
          overflow-y: auto;
        }

        .cf-city-work-panel {
          min-height: 0;
          max-height: none;
          overflow-y: auto;
          padding: 12px;
        }

        .cf-city-selected-land {
          align-items: flex-start;
          flex-direction: column;
          gap: 10px;
        }

        .cf-city-selected-land img {
          width: 100%;
          height: 145px;
        }

        .cf-city-action-list {
          grid-template-columns: 1fr;
        }

        .cf-city-work-button {
          min-height: 84px;
          padding: 12px;
        }

        .cf-city-work-button strong {
          font-size: 22px;
        }

        .cf-city-history-item {
          grid-template-columns: 1fr;
        }

        .cf-city-history-reward {
          flex-wrap: wrap;
        }
      }

      @media (max-width: 430px) {
        .cf-city-grid {
          grid-template-columns: 1fr;
        }

        .cf-city-topbar {
          grid-template-columns: 1fr;
        }

        .cf-city-refresh {
          grid-column: auto;
        }

        .cf-city-summary {
          grid-template-columns: 1fr;
        }

        .cf-city-land-list {
          max-height: 32dvh;
        }
      }
    `}</style>
  );
}